import {ChangeDetectionStrategy, Component, computed, input, output} from "@angular/core"
import {CdkMenuTrigger} from "@angular/cdk/menu"
import {
    AssetActionsMenuComponent,
    AssetCellChipComponent,
    AssetDownloadMenuComponent,
    AssetGridCellComponent,
    AssetMenuAction,
} from "@common/components/asset-grid"
import {LoadingSpinnerIconComponent} from "@common/components/progress/loading-spinner-icon/loading-spinner-icon.component"
import {JobState} from "@generated"
import type {MaterialAssetGridOutputFragment} from "@platform/components/materials/material-asset-grid/material-asset-grid/material-asset-grid.generated"

type ShaderBallResult = NonNullable<MaterialAssetGridOutputFragment["result"]>

type ShaderBallState = {kind: "empty"} | {kind: "loading"} | {kind: "failed"} | {kind: "complete"; result: ShaderBallResult}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-shaderball-asset-cell",
    templateUrl: "./shaderball-asset-cell.component.html",
    styleUrls: ["./shaderball-asset-cell.component.scss"],
    imports: [
        AssetActionsMenuComponent,
        AssetCellChipComponent,
        AssetDownloadMenuComponent,
        AssetGridCellComponent,
        CdkMenuTrigger,
        LoadingSpinnerIconComponent,
    ],
})
export class ShaderballAssetCellComponent {
    readonly $output = input<MaterialAssetGridOutputFragment | null | undefined>(null, {alias: "output"})
    readonly $pending = input<boolean>(false, {alias: "pending"})
    readonly $hasCyclesMaterial = input<boolean>(false, {alias: "hasCyclesMaterial"})
    readonly $canEdit = input<boolean>(false, {alias: "canEdit"})
    readonly $canDownload = input<boolean>(false, {alias: "canDownload"})

    readonly render = output<void>()
    readonly rerender = output<void>()
    readonly remove = output<void>()
    readonly cancel = output<void>()
    readonly retry = output<void>()
    readonly preview = output<void>()

    readonly $state = computed<ShaderBallState>(() => {
        const output = this.$output()
        const jobState = output?.job?.state
        if (this.$pending() || (jobState && isJobRunning(jobState))) return {kind: "loading"}
        if (!output) return {kind: "empty"}
        if (jobState === JobState.Failed || jobState === JobState.Cancelled) return {kind: "failed"}
        if (output.result) return {kind: "complete", result: output.result}
        return {kind: "empty"}
    })

    readonly actions: AssetMenuAction[] = [
        {id: "rerender", label: "Regenerate", icon: "fa-rotate-reverse"},
        {id: "remove", label: "Delete", icon: "fa-trash-can", variant: "destructive"},
    ]

    onAction(actionId: string) {
        if (actionId === "rerender") this.rerender.emit()
        else if (actionId === "remove") this.remove.emit()
    }
}

function isJobRunning(state: JobState): boolean {
    return state === JobState.Init || state === JobState.Runnable || state === JobState.Running
}
